import { useState, useEffect, useCallback } from 'react';
import { supabaseWithAuth } from '@/utils/supabaseWrapper';

export interface Statistics {
  nombre_campagnes: number;
  nombre_campagnes_trend: number;
  campagnes_en_attente: number;
  campagnes_en_attente_trend: number;
  taux_reponse: number;
  taux_reponse_trend: number;
  temps_gagne_minutes: number;
  temps_gagne_trend: number;
  delai_moyen_jours: number;
  delai_moyen_trend: number;
  nombre_relances: number;
  nombre_relances_trend: number;
  referents_contactes: number;
  referents_contactes_trend: number;
  note_moyenne: number;
  note_moyenne_trend: number;
}

export const useStatistics = () => {
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      
      // Récupérer l'utilisateur connecté
      const { data: { user } } = await supabaseWithAuth.auth.getUser();
      if (!user) throw new Error('Utilisateur non connecté');

      // Récupérer l'organization_id avec protection auth
      const { data: profile } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('profiles')
          .select('organization_id')
          .eq('id', user.id)
          .single();
      }, 'statistics.profile');

      if (!(profile as any)?.organization_id) {
        throw new Error('Organisation non trouvée');
      }

      // Appeler la fonction RPC avec protection auth
      const result = await supabaseWithAuth.rpc('get_organization_statistics', {
        p_org_id: (profile as any).organization_id
      });
      
      const { data, error: rpcError } = result as any;

      if (rpcError) throw rpcError;

      setStats(data as Statistics);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();

    // ⚠️ POLLING DÉSACTIVÉ pour éviter les re-renders inutiles
    // Les stats sont maintenant chargées uniquement au montage
    // Si besoin de rafraîchir, utiliser un bouton manuel ou un event
    
    // const interval = setInterval(fetchStats, 30000);
    // return () => clearInterval(interval);
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};
